// Teacher overview home. Greeting + the teacher's classes as cards + quick link
// to assignments. docs/replan-v2.md R9.

import { useT } from '@fajrarabi/i18n/react';
import { Button, Card, CardBody, Skeleton } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';
import { listTeacherClasses } from '~/lib/school';

export function TeacherHome() {
  const t = useT();
  const { user } = useAuth();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const name = user?.displayName ?? user?.email ?? '';

  const classes = useQuery({
    queryKey: ['teacher', 'classes'],
    queryFn: ({ signal }) => listTeacherClasses(signal),
  });

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">{t('app.teacher.welcome', { name })}</h1>
          <p className="mt-1 text-ink-700">{t('app.teacher.overview_subtitle')}</p>
        </div>
        <Link to="/$locale/teacher/assignments" params={{ locale }}>
          <Button variant="secondary">{t('app.teacher.assignments_title')}</Button>
        </Link>
      </div>

      <h2 className="mt-8 text-lg font-bold text-ink-900">{t('app.teacher.classes_title')}</h2>

      {classes.isLoading ? (
        <Skeleton className="mt-4 h-24 w-full" />
      ) : (classes.data?.length ?? 0) === 0 ? (
        <Card className="mt-4">
          <CardBody>
            <p className="text-sm text-ink-700">{t('app.teacher.no_classes')}</p>
          </CardBody>
        </Card>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {classes.data!.map((cls) => (
            <Link
              key={cls.id}
              to="/$locale/teacher/class/$classId"
              params={{ locale, classId: cls.id }}
              className="rounded-2xl bg-white p-5 shadow-[0_12px_30px_-20px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10 transition-shadow hover:shadow-[0_18px_40px_-22px_rgba(31,26,23,0.3)]"
            >
              <p className="text-lg font-bold text-ink-900">{cls.name}</p>
              <p className="mt-1 text-sm uppercase tracking-wide text-ink-500">{cls.grade}</p>
              <p className="mt-3 text-sm text-ink-700">
                {t('app.school.student_count', { n: cls.studentCount })}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
